// Per-page scoped search bar — same backend as the global command palette,
// but pinned to a single model and rendered as an inline dropdown directly
// under the input.
//
// Markup contract: see app/views/shared/_search_input.html.erb. An element
// with [data-scoped-search] containing an [data-scoped-search-input] gets
// auto-wired by `initScopedSearchbar()`.
//
//   data-scope="contact"             which model to query (must match a key
//                                     in GlobalSearchService::TYPES)
//   data-search-url="/api/v1/search"  endpoint
//   data-empty-text                  optional override
//   data-min-length                  default 2

const DEBOUNCE_MS = 200;

const escapeHtml = (str) =>
  String(str ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));

class ScopedSearchbar {
  constructor(root) {
    this.root = root;
    this.input = root.querySelector("[data-scoped-search-input]");
    this.results = root.querySelector("[data-scoped-search-results]");
    this.scope = root.dataset.scope;
    this.searchUrl = root.dataset.searchUrl;
    this.minLength = parseInt(root.dataset.minLength || "2", 10);
    this.emptyText = root.dataset.emptyText || "Aucun résultat.";

    this.activeIndex = -1;
    this.items = [];
    this.debounceTimer = null;
    this.activeAbort = null;

    this.bind();
  }

  bind() {
    this.input.addEventListener("input", () => this.scheduleSearch());
    this.input.addEventListener("focus", () => {
      if (this.input.value.trim().length >= this.minLength) this.open();
    });
    this.input.addEventListener("keydown", (e) => this.onKeydown(e));

    document.addEventListener("click", (event) => {
      if (!this.root.contains(event.target)) this.close();
    });

    this.results.addEventListener("mousemove", (event) => this.onMouseMove(event));
    this.results.addEventListener("click", (event) => this.onClick(event));
  }

  open()  { this.root.classList.add("is-open"); }
  close() { this.root.classList.remove("is-open"); this.activeIndex = -1; this.refreshActive(); }

  scheduleSearch() {
    clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();

    if (query.length < this.minLength) {
      if (this.activeAbort) this.activeAbort.abort();
      this.results.innerHTML = "";
      this.items = [];
      this.close();
      return;
    }

    this.results.innerHTML = `<div class="search-input__loading">Recherche…</div>`;
    this.open();
    this.debounceTimer = setTimeout(() => this.runSearch(query), DEBOUNCE_MS);
  }

  async runSearch(query) {
    if (this.activeAbort) this.activeAbort.abort();
    this.activeAbort = new AbortController();

    try {
      const url = `${this.searchUrl}?q=${encodeURIComponent(query)}&scope=${encodeURIComponent(this.scope)}`;
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.activeAbort.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (this.input.value.trim() !== query) return; // stale
      this.render(data, query);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("[scoped-search] failed", err);
      this.results.innerHTML = `<div class="search-input__empty">Erreur de recherche.</div>`;
    }
  }

  render(data, query) {
    const group = (data.groups || [])[0];
    if (!group || group.items.length === 0) {
      this.results.innerHTML = `<div class="search-input__empty">${escapeHtml(this.emptyText)}</div>`;
      this.items = [];
      this.activeIndex = -1;
      return;
    }

    this.results.innerHTML = group.items.map((item) => this.renderItem(item)).join("");
    this.items = Array.from(this.results.querySelectorAll(".search-input__result"));
    this.activeIndex = this.items.length > 0 ? 0 : -1;
    this.refreshActive();
  }

  renderItem(item) {
    const subtitle = item.subtitle
      ? `<span class="search-input__result-subtitle">${escapeHtml(item.subtitle)}</span>`
      : "";
    return `
      <a class="search-input__result" href="${escapeHtml(item.url)}">
        <span class="search-input__result-icon"><i class="${escapeHtml(item.icon || "fa-solid fa-circle")}"></i></span>
        <span class="search-input__result-content">
          <span class="search-input__result-title">${escapeHtml(item.title)}</span>
          ${subtitle}
        </span>
      </a>
    `;
  }

  refreshActive() {
    this.items.forEach((el, idx) => el.classList.toggle("is-active", idx === this.activeIndex));
    if (this.activeIndex >= 0) this.items[this.activeIndex]?.scrollIntoView({ block: "nearest" });
  }

  moveActive(delta) {
    if (this.items.length === 0) return;
    this.activeIndex = (this.activeIndex + delta + this.items.length) % this.items.length;
    this.refreshActive();
  }

  onKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.root.classList.contains("is-open")) this.open();
        this.moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveActive(-1);
        break;
      case "Enter":
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.activate(this.items[this.activeIndex]);
        }
        break;
      case "Escape":
        if (this.root.classList.contains("is-open")) {
          event.preventDefault();
          this.close();
        }
        break;
    }
  }

  onMouseMove(event) {
    const item = event.target.closest(".search-input__result");
    if (!item) return;
    const idx = this.items.indexOf(item);
    if (idx >= 0 && idx !== this.activeIndex) {
      this.activeIndex = idx;
      this.refreshActive();
    }
  }

  onClick(event) {
    const item = event.target.closest(".search-input__result");
    if (!item) return;
    event.preventDefault();
    this.activate(item);
  }

  activate(item) {
    if (!item) return;
    this.close();
    if (window.Turbo?.visit) window.Turbo.visit(item.href);
    else window.location.href = item.href;
  }
}

const initScopedSearchbar = () => {
  document.querySelectorAll("[data-scoped-search]").forEach((root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";
    new ScopedSearchbar(root);
  });
};

export { initScopedSearchbar };
